import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import AddModel from "../../../components/Add/AddModel";
import AddInputs from "../../../components/Add/AddInputs";
import AddBottomButton from "../../../components/Add/AddBottomButton";
import CustomModal from "../../../components/CustomModal";
import { ConfirmDeletePlanModal, DeleteSuccessModal } from "./DeletePlanModals";
import MainModel from "../../../components/Models/MainModel";
import Button from "../../../components/buttons/Button";
import Image from "../../../components/images/Image";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import CheckboxMap from "../../../components/checkbox/CheckboxMap";
import CurrencyInputField from "../../../components/inputfields/InputFieldWithCurrency";
import TextArea from "../../../components/inputfields/TextArea";
import InputLabel from "../../../components/inputfields/InputLabel";
import ErrorMessage from "../../../components/ErrorMessages/ErrorMessage";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import validateMandatoryFields from "../../../components/commonfunctions/validateMandatoryFields";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { RootState } from "../../../store";
import { bundlePlanForm, bundlePlanUpdate } from "../components/types";
import {
  bundlePlanInitialState,
  getBundlePlanInputs,
  MAX_PLAN_AMOUNT,
  PLAN_NAME_MAX_LENGTH,
  PLAN_TYPE_OPTIONS,
} from "./bundlePlanFields";

export default function AddBundlePlan({ edit }: { edit?: boolean }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, planId } = useParams();
  const { bundlePlans } = endpoints;
  const { getAPI, PostAPI, PatchAPI, DeleteAPI } = useApi();
  const { translate } = useTranslation(adminContents);
  const pathname = useSelector((state: RootState) => state?.geolocation?.previousUrl);

  const [state, setState] = useState<bundlePlanForm>(bundlePlanInitialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [planTypes, setPlanTypes] = useState<string[]>([]);
  const [planTypeError, setPlanTypeError] = useState("");
  const [planNameError, setPlanNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState(false);
  // How many remaining plans the server renumbered, from the delete response.
  const [resequencedCount, setResequencedCount] = useState(0);
  // Editing an existing plan asks for confirmation before saving or discarding.
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  // Set from the save response so the plan's USSD code can be confirmed back.
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [savedUssdPlanCode, setSavedUssdPlanCode] = useState("");

  const listingUrl = `/mno-management/list-of-mnos/view-mno/${id}/bundle-plans`;

  const handleDelete = async () => {
    setDeleteLoading(true);
    const response = await DeleteAPI(`${bundlePlans}/${planId}`);
    setDeleteLoading(false);
    setIsDeleteModalOpen(false);

    if (!response?.error) {
      // Returning to the listing refetches it, so the reordered USSD plan codes show.
      setResequencedCount(Number(response?.data?.data?.resequenced_plans_count) || 0);
      setIsDeletedModalOpen(true);
    } else {
      dispatch(
        showToast({
          message:
            response?.data?.response?.data?.message ||
            "Failed to delete the bundle plan. Please try again.",
          type: "error",
        })
      );
    }
    setDeleteLoading(false);
    setIsDeleteModalOpen(false);
  };

  const handleInputChange = (fieldId: string, value: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldId]: "",
    }));
    setState((prevState) => ({
      ...prevState,
      [fieldId]: value,
    }));
  };

  const handlePlanType = (option: string) => {
    setPlanTypeError("");
    setPlanTypes((prev) =>
      prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]
    );
  };

  const handlePlanName = (value: string) => {
    setPlanNameError("");
    setState((prevState) => ({
      ...prevState,
      plan_name: value,
    }));
  };

  /**
   * Validates every mandatory field along with the plan type selection, the
   * amount ceiling and the plan name length before building the payload.
   */
  const validate = () => {
    const mandatoryFields = [
      "bundle_id",
      "amount_excl_tax",
      "amount_incl_tax",
      "internal_subscription_fee",
    ];
    const errorObject = validateMandatoryFields(state, mandatoryFields);
    let isValid = Object.keys(errorObject).length === 0;

    if (!state.plan_name.trim()) {
      setPlanNameError("This field is required");
      isValid = false;
    } else if (state.plan_name.length > PLAN_NAME_MAX_LENGTH) {
      setPlanNameError(`Plan name cannot exceed ${PLAN_NAME_MAX_LENGTH} characters`);
      isValid = false;
    }

    if (planTypes.length === 0) {
      setPlanTypeError("Please select at least one plan type");
      isValid = false;
    }

    // The bundle ID is alphanumeric only. Surrounding whitespace is trimmed before
    // the check so a stray space is not reported as an invalid character.
    if (state.bundle_id.trim() && !/^[a-zA-Z0-9]+$/.test(state.bundle_id.trim())) {
      errorObject.bundle_id = "Bundle ID should contain only letters and numbers";
      isValid = false;
    }

    (["amount_excl_tax", "amount_incl_tax"] as const).forEach((field) => {
      if (errorObject[field]) return;
      const amount = Number(state[field]);
      if (amount <= 0) {
        errorObject[field] = "Amount should be greater than 0";
        isValid = false;
      } else if (amount > MAX_PLAN_AMOUNT) {
        errorObject[field] = `Amount cannot exceed AFA ${MAX_PLAN_AMOUNT}`;
        isValid = false;
      }
    });

    // The tax inclusive amount can never be lower than the exclusive one.
    if (
      !errorObject.amount_excl_tax &&
      !errorObject.amount_incl_tax &&
      Number(state.amount_incl_tax) < Number(state.amount_excl_tax)
    ) {
      errorObject.amount_incl_tax =
        "Amount including tax must be greater than or equal to amount excluding tax";
      isValid = false;
    }

    if (!errorObject.internal_subscription_fee && Number(state.internal_subscription_fee) < 0) {
      errorObject.internal_subscription_fee = "Internal subscription fee cannot be negative";
      isValid = false;
    } else if (
      !errorObject.internal_subscription_fee &&
      Number(state.internal_subscription_fee) > MAX_PLAN_AMOUNT
    ) {
      errorObject.internal_subscription_fee = `Internal subscription fee cannot exceed AFA ${MAX_PLAN_AMOUNT}`;
      isValid = false;
    }

    setErrors(errorObject);
    return isValid;
  };

  /**
   * Validates first so an invalid form surfaces its errors instead of a
   * confirmation dialog, then asks the admin to confirm before saving.
   */
  const handleSavePlan = () => {
    setErrors({});
    setPlanTypeError("");
    setPlanNameError("");
    if (!validate()) return;
    setIsSaveModalOpen(true);
  };

  const submitPlan = async () => {
    const editableFields: bundlePlanUpdate = {
      bundle_id: state.bundle_id.trim(),
      amount_excl_tax: Number(state.amount_excl_tax),
      amount_incl_tax: Number(state.amount_incl_tax),
      internal_subscription_fee: Number(state.internal_subscription_fee),
      plan_name: state.plan_name.trim(),
      // Keep the selection in the canonical Voice, Data, SMS order.
      plan_types: PLAN_TYPE_OPTIONS.filter((option) => planTypes.includes(option)),
    };

    // The confirmation dialog stays open while the request is in flight so its
    // Save button shows the loader; closing it first would leave the screen with
    // no feedback until the response lands.
    setLoading(true);
    // The edit endpoint rejects mno_id and ussd_plan_code: a plan cannot change MNO,
    // and its USSD code is already in circulation. Only send it when creating.
    const response = edit
      ? await PatchAPI(`${bundlePlans}/${planId}`, editableFields)
      : await PostAPI(bundlePlans, { ...editableFields, mno_id: id || "" });

    setLoading(false);
    setIsSaveModalOpen(false);

    if (!response?.error) {
      // Both responses carry the plan's USSD code: newly allocated when creating,
      // unchanged when editing. Editing falls back to the code already on the form.
      setSavedUssdPlanCode(
        response?.data?.data?.ussd_plan_code?.toString() || state.ussd_plan_code || ""
      );
      setIsSuccessModalOpen(true);
    } else {
      dispatch(
        showToast({
          message: response?.data?.response?.data?.message || "Something went wrong.",
          type: "error",
        })
      );
    }
  };

  const getPlanDetails = async () => {
    setEditLoading(true);
    const response = await getAPI(`${bundlePlans}/${planId}`);
    if (!response?.error) {
      const data = response?.data?.data;
      setState({
        ussd_plan_code: data?.ussd_plan_code?.toString() || "",
        bundle_id: data?.bundle_id?.toString() || "",
        amount_excl_tax: data?.amount_excl_tax?.toString() || "",
        amount_incl_tax: data?.amount_incl_tax?.toString() || "",
        internal_subscription_fee: data?.internal_subscription_fee?.toString() || "",
        plan_name: data?.plan_name || "",
      });
      setPlanTypes(Array.isArray(data?.plan_types) ? data.plan_types : []);
    } else {
      navigate("/not-found", { state: { route: listingUrl } });
    }
    setEditLoading(false);
  };

  useEffect(() => {
    if (edit && planId) {
      getPlanDetails();
    }
  }, []);

  return (
    <div>
      <Breadcrumbs
        navPath={[
          "MNO Management",
          "List of MNO’s",
          "View Details",
          "Bundle Plan List",
          `${edit ? "Edit" : "Create"} Plan`,
        ]}
        heading={`${edit ? "Edit" : "Create"} Bundle Plan`}
        noNavigation
        fullNavPath
        // One link per crumb so the indices stay aligned; the last crumb is the
        // current page and is never clickable.
        links={[
          pathname ? pathname : "/mno-management/list-of-mnos",
          pathname ? pathname : "/mno-management/list-of-mnos",
          `/mno-management/list-of-mnos/view-mno/${id}`,
          listingUrl,
          "",
        ]}
        hasAddPrivilege={true}
      />
      <AddModel>
        <h1 className="text-heading3 text-primary_text py-4">Bundle Plan Details</h1>
        <AddInputs
          inputs={getBundlePlanInputs(edit)}
          totalInputCount={5}
          inputValues={state}
          onInputChange={handleInputChange}
          mapping={true}
          errors={errors}
          editLoading={editLoading}
        />
        {/* Amount incl. Tax, Plan Type and the subscription fee share one row. They
            are rendered here rather than through AddInputs because AddInputs emits its
            own flex container, so its fields can never sit alongside these. */}
        <div className="-mx-5 flex flex-wrap w-full">
          <div className="px-5 w-full sm:w-1/2 lg:w-1/3 mb-5">
            <CurrencyInputField
              inputItem={{
                labelName: "Amount incl. Tax",
                id: "amount_incl_tax",
              }}
              value={state.amount_incl_tax}
              onChange={(event) => handleInputChange("amount_incl_tax", event.target.value)}
              errorMessage={errors.amount_incl_tax}
              required={true}
              tabIndex={0}
              min={0}
              max={MAX_PLAN_AMOUNT}
              editLoading={editLoading}
              customWidth="w-full"
              testId="amount_incl_tax"
            />
          </div>
          <div className="px-5 w-full sm:w-1/2 lg:w-1/3 mb-5">
            <InputLabel labelName="Plan Type" id="plan_type" required={true} />
            <CheckboxMap
              options={PLAN_TYPE_OPTIONS}
              checkedArray={planTypes}
              handelCheckBox={handlePlanType}
              error={planTypeError}
              className="gap-x-6"
            />
            {planTypeError && <ErrorMessage errorMessage={planTypeError} />}
          </div>
          <div className="px-5 w-full sm:w-1/2 lg:w-1/3 mb-5">
            <CurrencyInputField
              inputItem={{
                labelName: "Internal Subscription Fee",
                id: "internal_subscription_fee",
              }}
              value={state.internal_subscription_fee}
              onChange={(event) =>
                handleInputChange("internal_subscription_fee", event.target.value)
              }
              errorMessage={errors.internal_subscription_fee}
              required={true}
              tabIndex={0}
              min={0}
              max={MAX_PLAN_AMOUNT}
              editLoading={editLoading}
              // Without customWidth the field falls back to a fixed w-80.
              customWidth="w-full"
              testId="internal_subscription_fee"
            />
          </div>
        </div>
        <div className="w-full sm:pr-6">
          <TextArea
            id="plan_name"
            label="Plan Name"
            text={state.plan_name}
            setText={(value) => handlePlanName(value as string)}
            textError={planNameError}
            setTextError={setPlanNameError}
            maxLength={PLAN_NAME_MAX_LENGTH}
            placeholderRequied={false}
            required={true}
          />
        </div>
        <div className="w-full sm:pr-6 mt-2">
          <p className="bg-[#E5F0FC] text-primary_text text-body2 rounded-lg px-4 py-3">
            {edit
              ? "Note: The USSD Plan Code cannot be edited. If a Bundle Plan is deleted, the USSD Plan Codes will be reordered automatically."
              : "Note: The USSD Plan Code will be generated automatically once the new plan is saved"}
          </p>
        </div>
      </AddModel>
      <AddBottomButton
        buttonWidth={"w-[140px]"}
        buttonName="Save Plan"
        buttonClass={"w-[140px] whitespace-nowrap"}
        onClick={handleSavePlan}
        disabled={false}
        loading={loading}
        secondaryButtonName="Cancel"
        secondaryButtonClass="w-[120px] h-[45px] font-semibold text-small"
        secondaryButtonFunction={() => setIsCancelModalOpen(true)}
        // Deleting is only meaningful for a plan that already exists.
        tertiaryButtonName={edit ? "Delete" : undefined}
        tertiaryButtonClass="w-[120px] h-[45px] justify-center border border-negative !text-negative rounded-lg font-semibold"
        tertiaryButtonFunction={() => setIsDeleteModalOpen(true)}
        align="end"
        testID="save_bundle_plan"
      />
      {isSuccessModalOpen && (
        <MainModel
          modelWidth="w-full max-w-[454px]"
          disableOutsideClick
          handleClose={() => navigate(listingUrl)}
        >
          <div className="flex flex-col items-center">
            <Image src="success_1" objectFit="object-contain" testId="success-icon" />
            <h1 className="text-primary_text text-heading3 text-center pt-6">
              {translate(edit ? "Changes saved successfully" : "Bundle Plan Saved Successfuly")}
            </h1>
            {savedUssdPlanCode && (
              <p
                className="mt-6 bg-[#E5F0FC] text-primary_text text-body2 rounded-lg px-4 py-2"
                data-testid="created-ussd-plan-code"
              >
                {`${translate("USSD Plan Code")} : ${savedUssdPlanCode}`}
              </p>
            )}
            <div className="w-full flex justify-end mt-6">
              <Button
                buttonName={translate("Go Back")}
                buttonOnclick={() => navigate(listingUrl)}
                buttonWidth="w-fit"
                buttonHight="h-[45px]"
                className="px-5"
                textSize="text-small"
                testId="go-back"
              />
            </div>
          </div>
        </MainModel>
      )}
      {isSaveModalOpen && (
        <CustomModal
          isOpen={isSaveModalOpen}
          // Dismissing mid request would hide the loader and leave the save running.
          onClose={() => !loading && setIsSaveModalOpen(false)}
          onHandleClose={() => !loading && setIsSaveModalOpen(false)}
          closeOnOverlayClick={!loading}
          secondaryButtonDisabled={loading}
          title={edit ? "Save Changes?" : "Save Plan?"}
          subText={
            edit
              ? "Are you sure you want to save the changes you have made? Your updates will be applied to the plan."
              : "Are you sure you want to save this plan? Once saved, it will be available in the Bundle Plan list."
          }
          primaryButtonText={edit ? "Save Changes" : "Save"}
          secondaryButtonText="Cancel"
          handelSubmit={submitPlan}
          buttonLoading={loading}
          buttonWidth={edit ? "w-[140px]" : "w-[90px]"}
          reverseButton="justify-end"
        />
      )}
      {isCancelModalOpen && (
        <CustomModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onHandleClose={() => setIsCancelModalOpen(false)}
          title={edit ? "Cancel Changes ?" : "Cancel this plan?"}
          subText={
            edit
              ? "Are you sure you want to cancel your changes? Any unsaved changes will be lost."
              : "Are you sure you want to cancel this plan? Any information you've entered will be lost."
          }
          primaryButtonText="Yes, Cancel"
          secondaryButtonText={edit ? "Keep Editing" : "Keep Plan"}
          handelSubmit={() => {
            setIsCancelModalOpen(false);
            navigate(listingUrl);
          }}
          buttonWidth="w-[130px]"
          secondaryWidth="w-[140px]"
          reverseButton="justify-end"
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDeletePlanModal
          loading={deleteLoading}
          onCancel={() => !deleteLoading && setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
        />
      )}
      {isDeletedModalOpen && (
        <DeleteSuccessModal
          resequencedCount={resequencedCount}
          onGoBack={() => navigate(listingUrl)}
        />
      )}
    </div>
  );
}
